import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@maxiron/shared';
import { apiClient } from '../lib/api-client';
import { hardReloadWithCacheBust } from '../components/UpdateRequiredOverlay';
import { navModeFromPreferences } from '../components/layout/nav-sidebar-mode';
import { setStoredTheme } from '../lib/theme-storage';

interface AuthContextType {
  user: User | null;
  accessibleTenants: string[];
  selectedTenants: string[];
  isLoading: boolean;
  error: string | null;
  setSelectedTenants: (tenants: string[]) => void;
  updateTheme: (theme: 'light' | 'dark') => Promise<void>;
  updatePreferences: (preferences: Partial<User['preferences']>) => Promise<void>;
  logout: () => Promise<void>;
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessibleTenants, setAccessibleTenants] = useState<string[]>([]);
  const [selectedTenants, setSelectedTenantsState] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiClient.getMe();
      const navMode = navModeFromPreferences(data.user.preferences);
      const normalizedUser: User = {
        ...data.user,
        preferences: {
          ...data.user.preferences,
          navSidebarMode: navMode,
          navSidebarCollapsed: navMode === 'hidden',
        },
      };
      setUser(normalizedUser);
      setAccessibleTenants(data.accessibleTenants);
      setSelectedTenantsState(normalizedUser.preferences.selectedTenants || []);

      // On successful login (or session restore), do a one-time hard reload per browser session
      // to avoid stale asset cache issues after deploys.
      // Guarded to prevent loops.
      if (sessionStorage.getItem('portal:reloadedAfterLogin') !== 'true') {
        sessionStorage.setItem('portal:reloadedAfterLogin', 'true');
        // Delay a tick so state persists and the browser can render at least once.
        setTimeout(() => hardReloadWithCacheBust(), 0);
      }
      
      // Apply theme from user preferences (default to dark)
      const theme = normalizedUser.preferences.theme || 'dark';
      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      setStoredTheme(theme);
      localStorage.setItem('portal:navSidebarMode', navMode);
      localStorage.setItem('portal:navSidebarCollapsed', navMode === 'hidden' ? 'true' : 'false');
    } catch (err: any) {
      console.error('Failed to fetch user:', err);
      
      // If unauthorized (401), redirect to login immediately without showing error
      if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        window.location.href = '/login';
        return; // Exit early to prevent error state from being set
      }
      
      // For network/server errors, use the special error code
      if (err.message === 'SERVER_CONNECTION_ERROR' || err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setError('SERVER_CONNECTION_ERROR');
      } else {
      // For other errors, set error state
      setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Skip auth check on login and callback pages
    const path = window.location.pathname;
    if (path === '/login' || path === '/auth/callback') {
      setIsLoading(false);
      return;
    }
    
    fetchUser();
  }, []);

  const setSelectedTenants = async (tenants: string[]) => {
    setSelectedTenantsState(tenants);
    
    // Persist to backend
    try {
      await apiClient.updatePreferences({ selectedTenants: tenants });
      if (user) {
        setUser({ ...user, preferences: { ...user.preferences, selectedTenants: tenants } });
      }
    } catch (err) {
      console.error('Failed to update selected tenants:', err);
    }
  };

  const updateTheme = async (theme: 'light' | 'dark') => {
    try {
      // Apply theme immediately (optimistic update)
      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      setStoredTheme(theme);
      
      // Update backend
      await apiClient.updatePreferences({ theme });
      if (user) {
        setUser({ ...user, preferences: { ...user.preferences, theme } });
      }
    } catch (err) {
      console.error('Failed to update theme:', err);
      // Revert on error
      const currentTheme = user?.preferences.theme || 'light';
      if (currentTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  };

  const updatePreferences = async (preferences: Partial<User['preferences']>) => {
    try {
      const res = await apiClient.updatePreferences(preferences);
      const navMode = navModeFromPreferences(res.preferences);
      const normalizedPrefs = {
        ...res.preferences,
        navSidebarMode: navMode,
        navSidebarCollapsed: navMode === 'hidden',
      };
      if (user) {
        setUser({ ...user, preferences: normalizedPrefs });
      }
      localStorage.setItem('portal:navSidebarMode', navMode);
      localStorage.setItem('portal:navSidebarCollapsed', navMode === 'hidden' ? 'true' : 'false');
    } catch (err) {
      console.error('Failed to update preferences:', err);
    }
  };

  const logout = async () => {
    try {
      const response = await apiClient.logout();
      setUser(null);
      setAccessibleTenants([]);
      setSelectedTenantsState([]);
      
      // If response contains Azure logout URL, redirect there
      if (response && (response as any).logoutUrl) {
        window.location.href = (response as any).logoutUrl;
      } else {
        // Otherwise, just redirect to login
        window.location.href = '/login';
      }
    } catch (err) {
      console.error('Logout failed:', err);
      // Redirect to login anyway
      window.location.href = '/login';
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        accessibleTenants,
        selectedTenants,
        isLoading,
        error,
        setSelectedTenants,
        updateTheme,
        updatePreferences,
        logout,
        refetch: fetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}




